import React, {
  FC,
  UIEventHandler,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { LinkedinModalWrapper } from 'pages/accountDrawer/linkedinView/styles';
import { Prospect } from 'crono-fe-common/types/prospect';
import { Avatar, Typography } from '@mui/material';
import { format, isSameDay } from 'date-fns';
import { useAuth } from 'context/auth';
import {
  fullName,
  getError,
  getImageFromUrl,
  stringAvatarAccount,
} from 'crono-fe-common/utils';
import { useGetFullConversation } from 'hooks/services/conversation/useGetFullConversation';
import { ConversationMessage } from 'crono-fe-common/types/ConversationMessage';
import { LogLinkedin } from 'crono-fe-common/types/logLinkedin';
import { LinkedinLogsConversationWrapper } from './style';
import useGetLinkedinSettings from 'hooks/services/linkedinSettings/useGetLinkedinSettings';
import { colors } from 'crono-fe-common/theme';
import SentimentAnalysisBadge from 'crono-fe-common/components/SentimentAnalysisBadge';
import useRedirectToInbox from 'context/customLocation/redirectToInbox';
import { useConditionalSnackBar } from 'context/snackbar';
import { formatDateDivider } from 'utils/dateUtils';
import { Button } from '@crono-one/design-system';
import LinkedinMessageContent from 'components/LinkedinMessageContent';

interface LinkedinLogsConversationModalProps {
  prospect: Prospect;
  close: () => void;
  onRedirectToInbox?: () => void;
}

const LinkedinLogsConversationModal: FC<LinkedinLogsConversationModalProps> = ({
  prospect,
  close,
  onRedirectToInbox,
}) => {
  const { user } = useAuth();
  const {
    data: fullConversationPages,
    error: errorGetFullConversation,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetching,
  } = useGetFullConversation({
    limit: 20,
    prospectId: prospect.objectId,
    types: ['LinkedinInvitation', 'LinkedinMessage', 'LinkedinVoiceNote'],
  });
  const fullConversation = useMemo(() => {
    if (!fullConversationPages) return [];
    return fullConversationPages.pages.flatMap((page) => {
      return page.data.data;
    });
  }, [fullConversationPages]);

  const conversationMessages: (ConversationMessage & {
    linkedinLog: LogLinkedin;
  })[] = useMemo(() => {
    if (!fullConversation) return [];
    const messages = fullConversation.flatMap((page) => {
      return page.messages;
    });
    return messages
      .filter((message) => !!message.linkedinLog)
      .reverse() as (ConversationMessage & {
      linkedinLog: LogLinkedin;
    })[];
  }, [fullConversation]);

  const { data: linkedinSettings } = useGetLinkedinSettings();

  const scrollToBottom = () => {
    if (!messagesContainerRef.current) return;
    messagesContainerRef.current.scrollTop =
      messagesContainerRef.current.scrollHeight;
  };

  useEffect(() => {
    if (!isFetching) {
      scrollToBottom();
    }
  }, [isFetching]);

  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const [loadingMore, setLoadingMore] = useState(false);

  const getNextPage = async () => {
    if (!hasNextPage || loadingMore || !messagesContainerRef.current) return;
    setLoadingMore(true);
    const currentScrollHeight = messagesContainerRef.current.scrollHeight;

    const res = await fetchNextPage();
    if (res.data?.pages) {
      setTimeout(() => {
        setLoadingMore(false);
        if (!messagesContainerRef.current) return;
        messagesContainerRef.current.scrollTop =
          messagesContainerRef.current.scrollHeight - currentScrollHeight;
      }, 0);
    }
  };

  const handleScroll: UIEventHandler<HTMLDivElement> = (e) => {
    const top = e.currentTarget.scrollTop < 30;
    if (top && !loadingMore && hasNextPage) {
      getNextPage();
    }
  };

  const redirectToInbox = useRedirectToInbox({
    onRedirect: () => {
      close();
      if (onRedirectToInbox) {
        onRedirectToInbox();
      }
    },
  });

  useConditionalSnackBar([
    {
      condition: !!errorGetFullConversation,
      message:
        getError(errorGetFullConversation) ??
        'Error while retrieving the conversation',
      severity: 'error',
    },
  ]);

  return (
    <LinkedinModalWrapper
      style={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flex: 1,
        padding: 6,
      }}
    >
      <LinkedinLogsConversationWrapper
        ref={messagesContainerRef}
        onScroll={handleScroll}
      >
        {conversationMessages.map((message, index) => {
          const isSent = message.direction === 'Sent';
          const imageName = isSent
            ? fullName(user?.firstName, user?.lastName)
            : prospect.name || '';
          const imageUrl = isSent
            ? (linkedinSettings?.data?.data?.imageUrl ??
              (user?.company?.website &&
                getImageFromUrl(user?.company?.website, null)))
            : ((prospect.account?.website &&
                getImageFromUrl(prospect.account?.website, null)) ??
              undefined);

          //Calcolate the date to show in the divider
          let dateComponent = null;
          if (index === 0) {
            dateComponent = (
              <div className="date-divider">
                {formatDateDivider(message.eventDatetime)}
              </div>
            );
          } else {
            const previousMessage = conversationMessages[index - 1];
            const previousMessageDate = new Date(previousMessage.eventDatetime);
            const currentMessageDate = new Date(message.eventDatetime);

            if (!isSameDay(previousMessageDate, currentMessageDate)) {
              dateComponent = (
                <div className="date-divider">
                  {formatDateDivider(message.eventDatetime)}
                </div>
              );
            }
          }

          return (
            <React.Fragment key={index}>
              {dateComponent && (
                <>
                  <div className="day-separator" />
                  <Typography
                    fontSize={12}
                    lineHeight={'16px'}
                    fontWeight={500}
                    color={colors.grey11}
                    className="date-component"
                  >
                    {dateComponent}
                  </Typography>
                </>
              )}
              <div className="linkedin-logs-message">
                <Avatar
                  key={imageName}
                  src={imageUrl ?? undefined}
                  className={
                    imageUrl ? 'account-avatar white' : 'account-avatar'
                  }
                  style={{
                    width: 32,
                    height: 32,
                  }}
                >
                  {stringAvatarAccount(imageName || '')}
                </Avatar>
                <div className="text-container-container">
                  <div className="single-row">
                    <Typography
                      fontSize={14}
                      lineHeight={'18px'}
                      fontWeight={500}
                    >
                      {imageName}
                    </Typography>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      {message.linkedinLog.sentimentAnalysisResult && (
                        <SentimentAnalysisBadge
                          sentimentAnalysisResult={
                            message.linkedinLog.sentimentAnalysisResult
                          }
                          showLabel
                        />
                      )}
                      <Typography
                        fontSize={11}
                        lineHeight={'14px'}
                        fontWeight={500}
                        color={colors.grey11}
                      >
                        {message?.eventDatetime &&
                          format(new Date(message.eventDatetime), 'hh:mm a')}
                      </Typography>
                    </div>
                  </div>
                  <LinkedinMessageContent
                    content={message.linkedinLog.content}
                    renderText={(text) => (
                      <Typography
                        fontSize={14}
                        lineHeight={'24px'}
                        color={colors.grey1}
                        flexWrap={'wrap'}
                      >
                        {text}
                      </Typography>
                    )}
                  />
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </LinkedinLogsConversationWrapper>
      <Button
        className="w-fit m-1.5 min-w-35"
        onClick={() => redirectToInbox(prospect.objectId)}
      >
        Open inbox
      </Button>
    </LinkedinModalWrapper>
  );
};

export default LinkedinLogsConversationModal;
